import type {
  CheckoutBookingForm,
  CheckoutField,
  CheckoutTravelerDraft,
} from './checkout_source.js';
import type { AllocationDraft } from '../fields/allocation.js';

/** Seeds the allocation drafts from the form's server defaults — default
 *  company/cost center + the first project of the default company (matches the
 *  Flutter pre-selection).
 *
 *  Os defaults são SANEADOS contra o catálogo: um id que o form não conhece vira
 *  vazio em vez de virar um `value` sem `option` correspondente — o campo em
 *  branco e sem alternativas do #2459. Um id órfão que escapasse até o submit
 *  ainda derrubaria a reserva no fail-closed de `resolveAllocation`.
 *
 *  Um default fora do catálogo cai em VAZIO (estado honesto) em vez de virar
 *  seleção fantasma: o BFF já reconcilia o default com o catálogo antes de
 *  servir o form, então sobrar aqui significa que o item de fato não veio — e o
 *  campo de busca é quem resolve. */
export function seedAllocation(form: CheckoutBookingForm): AllocationDraft {
  // Sem default (colega sem centro de custo definido) → começa vazio; o requester
  // escolhe a empresa/centro no drawer (computeCanSubmit exige a escolha).
  const defaultCompany = form.companies.find((c) => c.id === form.defaultCompanyId);
  if (!defaultCompany) return { companyId: '', costCenterId: '', projectId: '' };
  // NÃO pré-selecionamos o centro de custo quando a empresa só tem um: o app real
  // também não o faz, e aqui isso teria efeito colateral — o julgamento do
  // prefill trata "sem centro de custo" como INCOMPLETO, e completá-lo sozinho
  // habilitaria a confirmação de um clique sem o viajante ter escolhido alocação
  // alguma (`confirmation_section`).
  const chosen = defaultCompany.costCenters.find((cc) => cc.id === form.defaultCostCenterId);
  return {
    companyId: defaultCompany.id,
    costCenterId: chosen?.id ?? '',
    projectId: defaultCompany.projects[0]?.id ?? '',
  };
}

/** Seeds the traveler draft — the on-file phone (the drawer's phone field is
 *  always required for flight) and, when the profile lacks a boarding name, the
 *  `firstName`/`lastName` the server derived from the civil name (#5809): the
 *  embark inputs already show them, and an empty draft kept the submit blocked
 *  on `embark` until the traveler retyped their own name. Everything else starts
 *  from the server profile via the field placeholders. */
export function seedTravelerDraft(form: CheckoutBookingForm): CheckoutTravelerDraft {
  const traveler = form.traveler;
  const embarkMissing = traveler?.missingRequired?.includes('embark') ?? false;
  return {
    ...(traveler?.phone ? { phone: traveler.phone } : {}),
    ...(embarkMissing && traveler?.firstName ? { boardingName: traveler.firstName } : {}),
    ...(embarkMissing && traveler?.lastName ? { boardingSurname: traveler.lastName } : {}),
  };
}

/** Whether the traveler identity editor should open on load — true when the
 *  server flags any editable identity field as missing on the profile, OR when
 *  the manifest requires `phone` and the seeded draft has none. O telefone nunca
 *  entra em `missingRequired` (quem o exige é o manifesto de voo/mobility), e com
 *  o editor fechado o único campo que trava o submit nem renderizava (#4206). */
export function travelerNeedsEditing(
  form: CheckoutBookingForm,
  fields: CheckoutField[],
  travelerDraft: CheckoutTravelerDraft,
): boolean {
  const missing = form.traveler?.missingRequired ?? [];
  const phoneMissing =
    fields.some((field) => field.kind === 'phone') && !travelerDraft.phone?.trim();
  return (
    phoneMissing ||
    missing.includes('birthdate') ||
    missing.includes('nationality') ||
    missing.includes('document') ||
    missing.includes('gender') ||
    missing.includes('embark')
  );
}
